// Normalization layer between the registry payloads and the console UI.
// Publishers ship wildly different shapes (flat identity hashes, nested
// `source_fields`, arrays of sub-records), so every value is projected into a
// typed field the display components can render without knowing the source.

const LABELS = Object.freeze({
  account_id: 'Conta',
  adapter_key: 'Adapter',
  bairro: 'Bairro',
  cargo: 'Cargo',
  categoria: 'Categoria',
  category: 'Categoria',
  cep: 'CEP',
  cidade: 'Cidade',
  cnj: 'CNJ',
  cnpj: 'CNPJ',
  cpf: 'CPF',
  created_at: 'Criado em',
  data_admissao: 'Admissão',
  data_ajuizamento: 'Ajuizamento',
  data_nascimento: 'Nascimento',
  dedupe_key: 'Chave de dedupe',
  descricao: 'Descrição',
  description: 'Descrição',
  email: 'E-mail',
  endereco: 'Endereço',
  estado: 'Estado',
  external_id: 'ID externo',
  fetched_at: 'Coletado em',
  field_schema: 'Schema de campos',
  id: 'ID',
  logradouro: 'Logradouro',
  lotacao: 'Lotação',
  matricula: 'Matrícula',
  metadata: 'Metadados',
  municipio: 'Município',
  name: 'Nome',
  nome: 'Nome',
  normalized_name: 'Nome normalizado',
  observed_at: 'Observado em',
  occurred_on: 'Ocorrido em',
  orgao: 'Órgão',
  orgao_julgador: 'Órgão julgador',
  person_id: 'ID da pessoa',
  person_key: 'Chave da pessoa',
  personal_data_fields: 'Campos pessoais',
  primary_matricula: 'Matrícula',
  query_contract: 'Contrato de consulta',
  record_kind: 'Tipo de registro',
  records_count: 'Registros',
  remuneracao: 'Remuneração',
  rg: 'RG',
  risk_level: 'Risco',
  run_id: 'ID do run',
  salario: 'Salário',
  sensitivity_level: 'Sensibilidade',
  sexo: 'Sexo',
  situacao: 'Situação',
  source_id: 'ID da fonte',
  source_key: 'Chave da fonte',
  source_name: 'Fonte',
  source_record_url: 'URL do registro',
  source_url: 'URL da fonte',
  status: 'Status',
  structured_record_type: 'Tipo estruturado',
  subject_key: 'Chave do assunto',
  subject_type: 'Tipo de assunto',
  summary: 'Resumo',
  telefone: 'Telefone',
  title: 'Título',
  tribunal: 'Tribunal',
  uf: 'UF',
  updated_at: 'Atualizado em',
  valor: 'Valor',
});

const DATE_KEY_PATTERN = /(^|_)(data|date)(_|$)|_at$|_on$|_em$/i;
const CURRENCY_KEY_PATTERN =
  /(valor|remunerac|salari|vencimento|preco|price|amount|bruto|liquido)/i;
const URL_PATTERN = /^https?:\/\//i;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(T|$)/;
const ONLY_DIGITS = /^\d+$/;

const titleize = value =>
  String(value)
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^\p{L}/u, character => character.toUpperCase());

export const humanizeFieldKey = key => {
  const normalized = String(key || '').trim();
  if (!normalized) return '';
  const direct = LABELS[normalized.toLowerCase()];
  if (direct) return direct;
  // Nested keys keep their path so two "nome" under different parents stay
  // distinguishable: "Classe › Nome".
  if (normalized.includes('.')) {
    return normalized
      .split('.')
      .map(part => humanizeFieldKey(part))
      .join(' › ');
  }
  return titleize(normalized);
};

export const formatCpf = value => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 11) return null;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const formatCnpj = value => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 14) return null;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

export const formatDateValue = value => {
  const raw = String(value ?? '');
  if (!ISO_DATE_PATTERN.test(raw)) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const withTime =
    raw.includes('T') && !raw.startsWith(`${raw.slice(0, 10)}T00:00:00`);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
};

export const formatCurrency = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numeric);
};

// A field is `{ key, label, type, text, href, items, raw }`. `type` drives the
// component: text | number | currency | date | boolean | link | list | json | empty.
export const buildField = (key, value) => {
  const label = humanizeFieldKey(key);
  const base = { key, label, raw: value };

  if (value === null || value === undefined || value === '') {
    return { ...base, type: 'empty', text: '—' };
  }
  if (typeof value === 'boolean') {
    return { ...base, type: 'boolean', text: value ? 'Sim' : 'Não' };
  }
  if (Array.isArray(value)) {
    const primitives = value.every(
      item => item === null || typeof item !== 'object'
    );
    if (!value.length) return { ...base, type: 'empty', text: '—' };
    if (primitives) {
      return {
        ...base,
        type: 'list',
        items: value.map(item => String(item)),
        text: value.join(', '),
      };
    }
    return { ...base, type: 'json', text: JSON.stringify(value, null, 2) };
  }
  if (typeof value === 'object') {
    return { ...base, type: 'json', text: JSON.stringify(value, null, 2) };
  }

  const text = String(value);
  if (URL_PATTERN.test(text)) {
    return { ...base, type: 'link', text, href: text };
  }
  if (/cpf/i.test(key)) {
    const formatted = formatCpf(text);
    if (formatted) return { ...base, type: 'text', text: formatted };
  }
  if (/cnpj/i.test(key)) {
    const formatted = formatCnpj(text);
    if (formatted) return { ...base, type: 'text', text: formatted };
  }
  if (DATE_KEY_PATTERN.test(key)) {
    const formatted = formatDateValue(text);
    if (formatted) return { ...base, type: 'date', text: formatted };
  }
  if (
    CURRENCY_KEY_PATTERN.test(key) &&
    ONLY_DIGITS.test(text.replace(/[.,]/g, ''))
  ) {
    const formatted = formatCurrency(text.replace(/\./g, '').replace(',', '.'));
    if (formatted) return { ...base, type: 'currency', text: formatted };
  }
  if (typeof value === 'number') {
    return {
      ...base,
      type: 'number',
      text: new Intl.NumberFormat('pt-BR').format(value),
    };
  }
  return { ...base, type: 'text', text };
};

const MAX_FLATTEN_DEPTH = 3;

// Nested publisher payloads become dotted paths so every leaf shows up as its
// own structured field instead of hiding inside a JSON blob. Arrays of objects
// and depth beyond the limit stay as JSON fields.
export const flattenPayload = (value, { prefix = '', depth = 0 } = {}) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return prefix ? { [prefix]: value } : {};
  }
  return Object.entries(value).reduce((accumulator, [key, nested]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const isPlainObject =
      nested !== null && typeof nested === 'object' && !Array.isArray(nested);
    if (isPlainObject && depth < MAX_FLATTEN_DEPTH) {
      return {
        ...accumulator,
        ...flattenPayload(nested, { prefix: path, depth: depth + 1 }),
      };
    }
    return { ...accumulator, [path]: nested };
  }, {});
};

const SOURCE_FIELDS_KEY = 'source_fields';

const pickFields = (payload, keys) =>
  keys
    .filter(key => payload[key] !== undefined)
    .map(key => buildField(key, payload[key]));

const PERSON_SECTIONS = Object.freeze([
  {
    id: 'identity',
    title: 'Identidade',
    icon: 'i-lucide-id-card',
    keys: [
      'nome',
      'name',
      'cpf',
      'matricula',
      'rg',
      'data_nascimento',
      'sexo',
      'nome_mae',
      'nome_pai',
    ],
  },
  {
    id: 'contact',
    title: 'Contato',
    icon: 'i-lucide-contact',
    keys: [
      'email',
      'telefone',
      'celular',
      'endereco',
      'cep',
      'municipio',
      'uf',
    ],
  },
  {
    id: 'registry',
    title: 'Registro',
    icon: 'i-lucide-database',
    keys: [
      'id',
      'person_key',
      'records_count',
      'normalized_name',
      'created_at',
      'updated_at',
    ],
  },
]);

const RECORD_SECTIONS = Object.freeze([
  {
    id: 'identification',
    title: 'Identificação',
    icon: 'i-lucide-file-text',
    keys: [
      'id',
      'title',
      'summary',
      'record_kind',
      'structured_record_type',
      'subject_type',
      'subject_key',
      'status',
      'risk_level',
    ],
  },
  {
    id: 'identity',
    title: 'Dados pessoais',
    icon: 'i-lucide-id-card',
    keys: ['nome', 'name', 'cpf', 'matricula', 'rg', 'data_nascimento'],
  },
  {
    id: 'provenance',
    title: 'Procedência',
    icon: 'i-lucide-route',
    keys: [
      'source_id',
      'source_key',
      'source_name',
      'run_id',
      'person_id',
      'dedupe_key',
      'external_id',
      'source_record_url',
      'occurred_on',
      'observed_at',
      'fetched_at',
      'created_at',
      'updated_at',
    ],
  },
]);

const SECTIONS_BY_KIND = Object.freeze({
  person: PERSON_SECTIONS,
  record: RECORD_SECTIONS,
});

// `item` is the raw API payload (already unminified for authorized operators).
// Returns display-ready groups; keys not claimed by a section land in
// "Campos da fonte" so nothing the API returned is ever dropped from the UI.
export const buildFieldGroups = (item, kind = 'record') => {
  if (!item || typeof item !== 'object') return [];

  const sections = SECTIONS_BY_KIND[kind] || RECORD_SECTIONS;
  const personal = {
    ...(item.personal_data_unminified || item.personalDataUnminified || {}),
  };
  const sourceFields = personal[SOURCE_FIELDS_KEY];
  delete personal[SOURCE_FIELDS_KEY];

  const minified = item.personal_data_minified || item.personalDataMinified;
  const identity = Object.keys(personal).length
    ? personal
    : { ...(minified || {}) };
  delete identity[SOURCE_FIELDS_KEY];

  const flatItem = {
    ...item,
    ...flattenPayload(identity),
  };
  delete flatItem.personal_data_unminified;
  delete flatItem.personal_data_minified;
  delete flatItem.personalDataUnminified;
  delete flatItem.personalDataMinified;
  delete flatItem.raw;
  delete flatItem.records;

  const claimed = new Set();
  const groups = sections
    .map(section => {
      const fields = pickFields(flatItem, section.keys);
      section.keys.forEach(key => claimed.add(key));
      return { ...section, fields };
    })
    .filter(group => group.fields.length);

  const publisherPayload = {
    ...flattenPayload(sourceFields || {}),
    ...flattenPayload(item.data || {}, { prefix: 'data' }),
  };
  const publisherFields = Object.entries(publisherPayload).map(([key, value]) =>
    buildField(key, value)
  );
  if (publisherFields.length) {
    groups.push({
      id: 'publisher',
      title: 'Campos da fonte',
      icon: 'i-lucide-braces',
      fields: publisherFields,
    });
  }

  const remainingKeys = Object.keys(flatItem).filter(
    key =>
      !claimed.has(key) &&
      key !== 'data' &&
      key !== 'personal_data_visibility' &&
      key !== 'metadata'
  );
  const remainingFields = pickFields(flatItem, remainingKeys);
  if (remainingFields.length) {
    groups.push({
      id: 'other',
      title: 'Outros campos',
      icon: 'i-lucide-list',
      fields: remainingFields,
    });
  }

  const metadataFields = Object.entries(
    flattenPayload(item.metadata || {})
  ).map(([key, value]) => buildField(key, value));
  if (metadataFields.length) {
    groups.push({
      id: 'metadata',
      title: 'Metadados',
      icon: 'i-lucide-settings-2',
      fields: metadataFields,
    });
  }

  return groups;
};

// Compact highlight line used by table rows: the few fields that identify the
// row at a glance, already formatted.
export const buildHighlightFields = (item, kind = 'record', limit = 4) => {
  const groups = buildFieldGroups(item, kind);
  const preferred = kind === 'person' ? ['identity', 'contact'] : ['identity'];
  const ordered = [
    ...groups.filter(group => preferred.includes(group.id)),
    ...groups.filter(group => !preferred.includes(group.id)),
  ];
  return ordered
    .flatMap(group => group.fields)
    .filter(field => field.type !== 'empty' && field.type !== 'json')
    .slice(0, limit);
};
